import { Link } from "gatsby"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import React, { useEffect, useState } from "react"

interface TableOfContentsProps {
  lang?: string
  position?: "left" | "right"
  headingDepth?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  avoidH1?: boolean
  img?: IGatsbyImageData
}

interface HeadingData {
  id: string
  text: string
  level: number
}

const title = {
  fr: "Sommaire",
  en: "Summary",
  de: "Inhaltsverzeichnis",
}

const TableOfContents: React.FC<TableOfContentsProps> = ({
  lang = "en",
  position = "left",
  headingDepth = "h2",
  avoidH1 = true,
  img,
}) => {
  const [headings, setHeadings] = useState<HeadingData[]>([])
  const [activeId, setActiveId] = useState<string>("")

  useEffect(() => {
    const headingTags = ["h1", "h2", "h3", "h4", "h5", "h6"]
    const startIndex = avoidH1 ? 1 : 0
    const endIndex = headingTags.indexOf(headingDepth) + 1

    const headingElements = Array.from(
      document.querySelectorAll(
        headingTags.slice(startIndex, endIndex).join(",")
      )
    )

    const headingData = headingElements
      .filter((heading) => {
        const isInFooter = heading.closest(
          'footer, [class*="footer"], [id*="footer"]'
        )
        return !isInFooter
      })
      .map((heading) => ({
        id: heading.id,
        text: heading.textContent || "",
        level: parseInt(heading.tagName[1]),
      }))

    setHeadings(headingData)

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: "-10% 0px -80% 0px" }
    )

    headingData.forEach((heading) => {
      const element = document.getElementById(heading.id)
      if (element) observer.observe(element)
    })

    return () => {
      headingData.forEach((heading) => {
        const element = document.getElementById(heading.id)
        if (element) observer.unobserve(element)
      })
    }
  }, [headingDepth, avoidH1])

  const positionClass = position === "left" ? "order-first" : "order-last"

  return (
    <nav className={`sticky top-28 w-full px-7 py-5 ${positionClass}`}>
      {img && (
        <>
          <GatsbyImage
            image={img}
            alt=""
            className="!absolute inset-0 h-full w-full object-cover"
          />
          <div className="!absolute inset-0 h-full w-full bg-fehr-800 opacity-80" />
        </>
      )}
      <div className="relative *:text-white">
        <p className="mb-2 text-3xl font-semibold">{title[lang]}</p>
        <ul className="space-y-1">
          {headings.map((heading) => (
            <li
              key={heading.id}
              className={`pl-${(heading.level - (avoidH1 ? 2 : 1)) * 2}`}
            >
              <Link
                to={`#${heading.id}`}
                className={`block py-1 capitalize ${
                  activeId === heading.id
                    ? "text-primary font-medium"
                    : "text-white hover:text-gray-200"
                }`}
              >
                {heading.text}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  )
}

export default TableOfContents
